import { BikeRelay40YearsPoint } from '../types';
import { format, isAfter, isBefore, isEqual, isSameDay, parseISO, set } from 'date-fns';
import { assetPrefix } from '../../../../utils';

const createMarkerIcon = (width: number, point: BikeRelay40YearsPoint) => {
  const iconDiv = document.createElement('div');
  const glyphImg = document.createElement('img');
  const glyphImgBike = document.createElement('img');

  glyphImg.src = `${assetPrefix}/assets/bike-relay-40-years/svg/marker-pulse.svg`;
  glyphImg.style.marginBottom = '-5px';
  glyphImg.style.width = `${width}px`;

  iconDiv.innerHTML = glyphImg.outerHTML;

  if (point.showBikeIcon) {
    glyphImgBike.src = `${assetPrefix}/assets/bike-relay-40-years/svg/bicycle-solid.svg`;
    glyphImgBike.style.position = 'absolute';
    glyphImgBike.style.width = `${width + 10}px`;

    iconDiv.innerHTML += glyphImgBike.outerHTML;
  }

  return iconDiv;
};
// Helper function to create a single marker
const createMarker = (
  point: BikeRelay40YearsPoint,
  map: google.maps.Map,
  infoWindow: google.maps.InfoWindow
) => {
  const iconDiv = createMarkerIcon(40, point);
  const gMarker = new google.maps.marker.AdvancedMarkerElement({
    position: { lat: point.latitude || 0, lng: point.longitude || 0 },
    content: iconDiv,
    gmpClickable: true,
    title: point.vanVestiging,
    map: map
  });

  const updateMarkerWidth = () => {
    const zoom = map.getZoom();
    let width = 40; // Default width

    if (zoom && zoom < 10) {
      // Scale down width when zooming out below 10
      width = 40 - (10 - zoom) * 8; // Adjust multiplier (2) for scaling rate
      if (width < 15) {
        // Set a minimum width
        width = 15;
      }
    }

    gMarker.content = createMarkerIcon(width, point);
  };
  // Initial scale update
  updateMarkerWidth();

  // Add zoom change listener
  map.addListener('zoom_changed', updateMarkerWidth);
  gMarker.addListener('click', () => {
    if (map) {
      const position = gMarker.position;
      if (position) {
        map.panTo(position);
        map.setZoom(12);
        infoWindow.close();
        infoWindow.setContent(
          `${format(new Date(point.datumVolgendeVestiging), 'dd-MM-yyyy')} - ${point.kilometers} km`
        );
        infoWindow.setHeaderContent(gMarker.title);
        //infoWindow.setHeaderDisabled(true);
        infoWindow.open(map, gMarker);
      } else {
        console.error('Marker position is invalid (null or undefined)');
      }
    }
  });

  return gMarker;
};

// Helper function to create a single path segment
const createPathSegment = (
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
  color: string,
  map: google.maps.Map
) => {
  const segmentPath = [start, end];

  new google.maps.Polyline({
    path: segmentPath,
    geodesic: true,
    strokeColor: color,
    strokeOpacity: 1.0,
    strokeWeight: 4,
    map: map
  });
};

// Helper function to create a single arrow on a path segment
const createArrow = (
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
  color: string,
  map: google.maps.Map
) => {
  const segmentPath = [start, end];

  const arrowSymbol = {
    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 4,
    strokeColor: color,
    fillColor: color,
    fillOpacity: 1
  };

  new google.maps.Polyline({
    path: segmentPath,
    geodesic: true,
    strokeColor: 'transparent',
    strokeOpacity: 0,
    strokeWeight: 0,
    icons: [
      {
        icon: arrowSymbol,
        offset: '25%',
        repeat: '50%'
      }
    ],
    map: map
  });
};

export const bikeRelay40YearsCreateMapElements = (
  relayPoints: BikeRelay40YearsPoint[],
  map: google.maps.Map
) => {
  const infoWindow = new google.maps.InfoWindow();
  relayPoints.forEach((point, index) => {
    // Create marker
    createMarker(point, map, infoWindow);

    // Create paths and arrows if not the last point
    if (index < relayPoints.length - 1) {
      const nextPoint = relayPoints[index + 1];
      const start = { lat: point.latitude || 0, lng: point.longitude || 0 };
      const end = {
        lat: nextPoint.latitude || 0,
        lng: nextPoint.longitude || 0
      };
      const color = point.isCurrent ? '#ff5e3a' : point.isPassed ? '#bfbfbf' : '#00325d';

      createPathSegment(start, end, color, map);
      createArrow(start, end, color, map);
    }
  });
};

export const bikeRelay40YearsGetNow = () => {
  let now = new Date();
  now = set(now, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
  const targetDate = parseISO('2025-03-04');

  if (isBefore(now, targetDate)) {
    now = targetDate;
  }

  const dateParam = new URLSearchParams(window.location.search).get('date');
  if (dateParam) {
    try {
      now = parseISO(dateParam); // Use parseISO from date-fns
      console.log(now);
    } catch (e) {
      console.log('Error parsing date param', e);
    }
  }

  return now;
};

export const processRelayPoints = (res: BikeRelay40YearsPoint[], now: Date) => {
  let totalDistance = 0;
  let traveledDistance = 0;
  let officesRelayed = 0;
  let foundCurrent = false;
  let showBikeIcon = false;
  const currentRelayPoints: BikeRelay40YearsPoint[] = [];

  const relayPoints = res.map((point) => {
    totalDistance += point.kilometers || 0;

    const arrivalDate = parseISO(point.datumAankomst);
    const nextOfficeArrivalDate = parseISO(point.datumVolgendeVestiging);

    function isNowBetweenArrivalAndNextOffice(now: Date, arrivalDate: Date, nextOfficeDate: Date) {
      return (
        (isEqual(now, arrivalDate) || isAfter(now, arrivalDate)) &&
        (isEqual(now, nextOfficeDate) || isBefore(now, nextOfficeDate))
      );
    }

    if (isNowBetweenArrivalAndNextOffice(now, arrivalDate, nextOfficeArrivalDate)) {
      foundCurrent = true;
      if (!showBikeIcon) {
        point.showBikeIcon = true;
        showBikeIcon = true;
      }
      currentRelayPoints.push(point);
      point.isCurrent = true;
      point.isSameDay = isSameDay(now, arrivalDate) && isSameDay(now, nextOfficeArrivalDate); // Use isSameDay from date-fns
      if (!point.isSameDay && currentRelayPoints.length > 1) {
        currentRelayPoints.pop();
        point.isCurrent = false;
      }
    }

    if (isAfter(now, arrivalDate) && isAfter(now, nextOfficeArrivalDate)) {
      point.isPassed = true;
      traveledDistance += point.kilometers || 0;
      officesRelayed += 1;
    } else {
      point.isActive = true;
    }

    return point;
  });

  return {
    relayPoints,
    totalDistance,
    traveledDistance,
    officesRelayed,
    foundCurrent,
    currentRelayPoints
  };
};
